import { useState } from 'react';
import { Link } from 'react-router';
import {
  FileText,
  ShieldCheck,
  BarChart3,
  CheckCircle2,
  Sparkles,
  AlertTriangle,
  FileCheck,
  FileSearch,
} from 'lucide-react';

const tabs = [
  {
    id: 'vergabedokumente',
    label: 'KI-gestützte Vergabedokumente',
    icon: FileText,
    headline:
      'Erstellen Sie Leistungsbeschreibungen wie gewohnt, nur schneller.',
    bullets: [
      'Automatische Erstellung von Leistungsverzeichnissen und Vergabeunterlagen',
      'Nutzung Ihrer bestehenden Mustervorlagen und Textbausteine',
      'Einbindung bewährter Formulierungen aus früheren Verfahren',
      'Automatische Einhaltung von VOB/A, UVgO und hausinternen Richtlinien',
    ],
    mockup: 'generate',
  },
  {
    id: 'qualitaetspruefung',
    label: 'KI-gestützte Qualitätsprüfung',
    icon: ShieldCheck,
    headline:
      'Machen Sie Ihre Leistungsbeschreibungen wasserdicht, bevor sie veröffentlicht werden.',
    bullets: [
      'Echtzeitanalyse anhand von über 100 Qualitätskriterien',
      'Erkennung mehrdeutiger Formulierungen, die zu Nachprüfungsverfahren führen können',
      'Prüfung auf Konformität mit VOB/A, UVgO, GWB und hausinternen Richtlinien',
      'KI-gestützte Umformulierungsvorschläge direkt im Dokument',
    ],
    mockup: 'quality',
  },
  {
    id: 'angebotsbewertung',
    label: 'KI-gestützte Angebotsbewertung',
    icon: BarChart3,
    headline:
      'Beschleunigen Sie Ihre Angebotsprüfung, ohne Abstriche bei der Rechtssicherheit.',
    bullets: [
      'Automatische Prüfung auf formale und inhaltliche Vollständigkeit',
      'Unterstützung beim Abgleich der Angebote mit Ihren Zuschlagskriterien',
      'Vorbereitung von Aufklärungsfragen für die engere Wahl',
      'Erstellung von Bewertungsberichten und revisionssicherer Dokumentation',
    ],
    mockup: 'evaluation',
  },
] as const;

function GenerateMockup() {
  const sections = [
    { title: 'Los 1: Elektroinstallation', status: 'done' },
    { title: 'Los 2: Sanitärtechnik', status: 'done' },
    { title: 'Los 3: Lüftungstechnik', status: 'active' },
  ];
  return (
    <div className="bg-muted/30 rounded-lg p-5 h-full flex flex-col gap-4">
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        <Sparkles className="w-3.5 h-3.5" />
        Dokument wird generiert
      </div>
      <div className="border rounded-lg bg-white flex-1 overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/20 flex items-center justify-between">
          <span className="text-sm font-medium text-[#111]">
            Leistungsverzeichnis — Neubau Grundschule
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
            Aus Vorlage
          </span>
        </div>
        <div className="p-4 space-y-3">
          {sections.map((s) => (
            <div
              key={s.title}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                s.status === 'active'
                  ? 'border-primary/30 bg-primary/5'
                  : 'border-border'
              }`}
            >
              {s.status === 'done' ? (
                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin shrink-0" />
              )}
              <span className="text-sm text-[#111]">{s.title}</span>
              {s.status === 'active' && (
                <span className="ml-auto text-[10px] text-primary font-medium">
                  Generiert...
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="px-4 pb-4 space-y-2">
          <div className="h-2.5 bg-muted rounded w-full" />
          <div className="h-2.5 bg-muted rounded w-5/6" />
          <div className="h-2.5 bg-muted rounded w-4/6" />
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <FileText className="w-3.5 h-3.5" />
        <span>3 Vorlagen · 12 Textbausteine verwendet</span>
      </div>
    </div>
  );
}

function QualityMockup() {
  const checks = [
    {
      label: 'VOB/A-Konformität',
      status: 'pass',
      detail: '§ 7, § 7a — bestanden',
    },
    {
      label: 'UVgO-Konformität',
      status: 'pass',
      detail: '§ 23, § 28 — bestanden',
    },
    {
      label: 'Eindeutigkeit der Leistung',
      status: 'warn',
      detail: '2 mehrdeutige Formulierungen',
    },
    {
      label: 'Vollständigkeit Eignungskriterien',
      status: 'pass',
      detail: 'Alle Kriterien definiert',
    },
    {
      label: 'Fristensetzung',
      status: 'warn',
      detail: 'Angebotsfrist unter Minimum',
    },
    {
      label: 'Hausinterne Richtlinien',
      status: 'pass',
      detail: 'Alle Vorgaben eingehalten',
    },
  ];
  return (
    <div className="bg-card border rounded-lg h-full flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <span className="text-sm font-medium text-[#111]">
          Qualitätsbericht
        </span>
        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
          94 / 100 Punkte
        </span>
      </div>
      <div className="p-3 space-y-1.5 flex-1">
        {checks.map((c) => (
          <div
            key={c.label}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm ${
              c.status === 'warn' ? 'bg-amber-50' : 'bg-muted/20'
            }`}
          >
            {c.status === 'pass' ? (
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            )}
            <span className="font-medium text-[#111] truncate">{c.label}</span>
            <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
              {c.detail}
            </span>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 border-t bg-amber-50/50 flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-[#111]">KI-Vorschlag:</span>{' '}
          &quot;Austausch der Fenster&quot; → &quot;Austausch von 42 Kunststofffenstern
          gem. Pos. 3.2.1&quot;
        </p>
      </div>
    </div>
  );
}

function EvaluationMockup() {
  const bids = [
    {
      name: 'Bau GmbH Berlin',
      price: '1.245.800 €',
      formal: true,
      score: 87,
    },
    {
      name: 'Haustechnik Müller',
      price: '1.312.400 €',
      formal: true,
      score: 79,
    },
    {
      name: 'Elektro Schmidt AG',
      price: '1.189.200 €',
      formal: false,
      score: null,
    },
  ];
  return (
    <div className="bg-card border rounded-lg h-full flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <span className="text-sm font-medium text-[#111]">
          Angebotsübersicht
        </span>
        <span className="text-xs text-muted-foreground">
          3 Angebote eingegangen
        </span>
      </div>
      <div className="p-3 space-y-2 flex-1">
        {bids.map((b) => (
          <div
            key={b.name}
            className={`rounded-lg border p-3 ${!b.formal ? 'border-red-200 bg-red-50/50' : 'border-border'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#111]">{b.name}</span>
              <span className="text-sm font-semibold text-[#111]">
                {b.price}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                {b.formal ? (
                  <FileCheck className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                )}
                <span
                  className={`text-xs ${b.formal ? 'text-green-600' : 'text-red-600'}`}
                >
                  {b.formal ? 'Formal vollständig' : 'Unterlagen fehlen'}
                </span>
              </div>
              {b.score !== null && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${b.score}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-[#111]">
                    {b.score}%
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 border-t bg-muted/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSearch className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs text-muted-foreground">
            2 Aufklärungsfragen vorbereitet
          </span>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
          Bericht bereit
        </span>
      </div>
    </div>
  );
}

export function FeaturesTabs() {
  const [activeTab, setActiveTab] = useState(0);
  const tab = tabs[activeTab]!;
  const Icon = tab.icon;

  return (
    <section
      id="features"
      className="flex flex-col items-center justify-center w-full relative py-20"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-secondary via-background to-background" />
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#111] text-balance mb-4">
            Unser Angebot
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-balance">
            Entwickelt für Vergabestellen, die mehr schaffen wollen.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-3 mb-10">
          {tabs.map((t, i) => {
            const TabIcon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(i)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer border ${
                  activeTab === i
                    ? 'bg-primary text-primary-foreground border-transparent'
                    : 'bg-card text-[#111] border-border hover:bg-muted/50'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="bg-card border rounded-xl p-8 md:p-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-[#111] mb-6">
                {tab.headline}
              </h3>
              <ul className="space-y-3">
                {tab.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-[#111]">
                      {bullet}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="min-h-[300px]">
              {tab.mockup === 'generate' && <GenerateMockup />}
              {tab.mockup === 'quality' && <QualityMockup />}
              {tab.mockup === 'evaluation' && <EvaluationMockup />}
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-10">
          <Link to="/demo">
            <button className="inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent text-sm font-medium whitespace-nowrap transition-all outline-none select-none active:translate-y-px bg-primary text-primary-foreground h-10 gap-1.5 px-5 cursor-pointer hover:bg-primary/90">
              Demo anfragen
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}
